import type { RawAttribute, SpatialAnchor, WallSegment } from '@manudev/model';
import { isType, type Storey } from '../props';
import type { BuildStore } from '../types';
import { boxesByElement, nativeLength } from './frame';
import { elevationOf, orderedStoreys, sourceStorey } from './storeys';
import type { BuildParams, BuildResult } from './types';

// The void runs past both wall faces so the cut is clean rather than coplanar with them.
const MARGIN = 0.05;
const LEAF = 0.05;
const DEFAULT_THICKNESS = 0.2;
const DOOR = { width: 0.9, height: 2.1, sill: 0 };
const WINDOW = { width: 1.2, height: 1.4, sill: 0.9 };

export interface OpeningSpec {
  wall: WallSegment;
  // distance from the wall's start to the middle of the opening, in metres
  offset: number;
  width: number;
  height: number;
  sill: number;
  kind: 'door' | 'window';
  name?: string;
}

export interface OpeningIds {
  openingId: number;
  fillId: number;
}

// A door or a window cut into a wall that is already there: the IfcOpeningElement voids the
// wall and the leaf fills the opening. ifc-lite has no in-store builder for either, so both
// are hand-emitted against the storey placement; the CSG only needs the void to overlap the
// wall in world space, not to hang off the wall's own placement.
export async function cutOpening(
  store: BuildStore,
  anchor: SpatialAnchor,
  spec: OpeningSpec,
): Promise<OpeningIds> {
  const [ax, ay] = axis(spec.wall);
  const nx = -ay;
  const ny = ax;
  const thickness = spec.wall.thickness ?? DEFAULT_THICKNESS;
  const cx = spec.wall.start[0] + ax * spec.offset;
  const cy = spec.wall.start[1] + ay * spec.offset;
  const owner = anchor.ownerHistoryId != null ? `#${anchor.ownerHistoryId}` : null;
  const ifc2x3 = (anchor.schema ?? 'IFC4') === 'IFC2X3';
  const metres = (v: number): RawAttribute => ({ real: nativeLength(anchor, v) });
  const ratio = (v: number): RawAttribute => ({ real: v });
  const raw = (type: string, attributes: RawAttribute[]) =>
    store.addRaw(type, attributes, { product: false });

  // Local Z runs through the wall and local X points up, which leaves local Y along the wall:
  // the frame the Duplex's own Revit openings use.
  const shapeOf = async (depth: number) => {
    const origin = await raw('IfcCartesianPoint', [[metres(0), metres(0), metres(0)]]);
    const through = await raw('IfcDirection', [[ratio(nx), ratio(ny), ratio(0)]]);
    const up = await raw('IfcDirection', [[ratio(0), ratio(0), ratio(1)]]);
    const frame = await raw('IfcAxis2Placement3D', [`#${origin}`, `#${through}`, `#${up}`]);
    const middle = await raw('IfcCartesianPoint', [[metres(0), metres(0)]]);
    const position = await raw('IfcAxis2Placement2D', [`#${middle}`, null]);
    const profile = await raw('IfcRectangleProfileDef', [
      '.AREA.',
      null,
      `#${position}`,
      metres(spec.height),
      metres(spec.width),
    ]);
    const extrude = await raw('IfcDirection', [[ratio(0), ratio(0), ratio(1)]]);
    const solid = await raw('IfcExtrudedAreaSolid', [
      `#${profile}`,
      `#${frame}`,
      `#${extrude}`,
      metres(depth),
    ]);
    const body = await raw('IfcShapeRepresentation', [
      `#${anchor.bodyContextId}`,
      'Body',
      'SweptSolid',
      [`#${solid}`],
    ]);
    return raw('IfcProductDefinitionShape', [null, null, [`#${body}`]]);
  };

  const placementOf = async (back: number) => {
    const at = await raw('IfcCartesianPoint', [
      [metres(cx - nx * back), metres(cy - ny * back), metres(spec.sill + spec.height / 2)],
    ]);
    const local = await raw('IfcAxis2Placement3D', [`#${at}`, null, null]);
    return raw('IfcLocalPlacement', [`#${anchor.storeyPlacementId}`, `#${local}`]);
  };

  const name = spec.name ?? (spec.kind === 'door' ? 'Door' : 'Window');
  const voidShape = await shapeOf(thickness + 2 * MARGIN);
  const voidPlacement = await placementOf(thickness / 2 + MARGIN);
  // IFC2X3 stops at Tag; IFC4 adds PredefinedType to IfcOpeningElement.
  const openingId = await raw('IfcOpeningElement', [
    await store.guid(),
    owner,
    `${name} opening`,
    null,
    'Opening',
    `#${voidPlacement}`,
    `#${voidShape}`,
    null,
    ...(ifc2x3 ? [] : ['.OPENING.']),
  ]);
  await raw('IfcRelVoidsElement', [
    await store.guid(),
    owner,
    null,
    null,
    `#${spec.wall.id}`,
    `#${openingId}`,
  ]);

  const leafShape = await shapeOf(LEAF);
  const leafPlacement = await placementOf(LEAF / 2);
  // IFC2X3 stops at OverallWidth; IFC4 adds the two type enums and their free-text twin.
  const tail: RawAttribute[] = ifc2x3
    ? []
    : spec.kind === 'door'
      ? ['.DOOR.', '.SINGLE_SWING_LEFT.', null]
      : ['.WINDOW.', '.SINGLE_PANEL.', null];
  const fillId = await store.addRaw(spec.kind === 'door' ? 'IfcDoor' : 'IfcWindow', [
    await store.guid(),
    owner,
    name,
    null,
    null,
    `#${leafPlacement}`,
    `#${leafShape}`,
    null,
    metres(spec.height),
    metres(spec.width),
    ...tail,
  ]);
  await raw('IfcRelFillsElement', [
    await store.guid(),
    owner,
    null,
    null,
    `#${openingId}`,
    `#${fillId}`,
  ]);
  await raw('IfcRelContainedInSpatialStructure', [
    await store.guid(),
    owner,
    null,
    null,
    [`#${fillId}`],
    `#${anchor.storeyId}`,
  ]);
  return { openingId, fillId };
}

// One opening into one wall. Without a host it takes the longest wall of the storey, and
// without a size it copies the storey below's first opening of the same kind.
export async function addOpening(store: BuildStore, params: BuildParams): Promise<BuildResult> {
  const kind = params.kind === 'window' ? 'window' : 'door';
  const storeys = await orderedStoreys(store);
  const storey = await hostStorey(store, storeys, params);
  const segments = await store.wallSegments(storey.expressID);
  if (!segments.length) throw new Error(`no wall axes on ${storey.name}`);
  const wall =
    params.host != null
      ? segments.find((s) => s.id === params.host)
      : [...segments].sort((a, b) => span(b) - span(a))[0];
  if (!wall) throw new Error(`no wall #${params.host} with an axis on ${storey.name}`);

  const length = span(wall);
  const copied = await openingBelow(store, storeys, storey, kind);
  const width = Math.min(params.width ?? copied.width, Math.max(length - 0.2, 0.2));
  const height = params.height ?? copied.height;
  const sill = params.sill ?? copied.sill;
  const offset = clamp(params.offset ?? length / 2, width / 2, length - width / 2);

  const anchor = await store.anchor(storey.expressID);
  const { fillId } = await cutOpening(store, anchor, {
    wall,
    offset,
    width,
    height,
    sill,
    kind,
    ...(params.name ? { name: params.name } : {}),
  });
  return {
    created: [fillId],
    removed: [],
    note: `${width.toFixed(2)} by ${height.toFixed(2)} m ${kind} cut into wall #${wall.id} on ${storey.name}, ${offset.toFixed(2)} m along it`,
  };
}

async function hostStorey(
  store: BuildStore,
  storeys: Storey[],
  params: BuildParams,
): Promise<Storey> {
  if (params.host == null) return sourceStorey(store, params.storey);
  const host = (await store.elements()).find((e) => e.expressID === params.host);
  const found = storeys.find((s) => s.expressID === host?.storeyId);
  if (!found) throw new Error(`no wall #${params.host} in the model`);
  return found;
}

interface Leaf {
  width: number;
  height: number;
  sill: number;
}

async function openingBelow(
  store: BuildStore,
  storeys: Storey[],
  storey: Storey,
  kind: 'door' | 'window',
): Promise<Leaf> {
  const fallback = kind === 'door' ? DOOR : WINDOW;
  const below = [...storeys].reverse().find((s) => elevationOf(s) < elevationOf(storey) - 0.01);
  if (!below) return fallback;
  const type = kind === 'door' ? 'IfcDoor' : 'IfcWindow';
  const found = (await store.elements()).filter(
    (e) => e.storeyId === below.expressID && isType(e, type),
  );
  if (!found.length) return fallback;
  const boxes = boxesByElement(await store.mesh(found.map((e) => e.expressID)), elevationOf(below));
  const box = boxes.get(found[0]!.expressID);
  if (!box) return fallback;
  return {
    width: Math.max(box.max[0] - box.min[0], box.max[1] - box.min[1], 0.2),
    height: Math.max(box.max[2] - box.min[2], 0.2),
    sill: Math.max(box.min[2], 0),
  };
}

function axis(wall: WallSegment): [number, number] {
  const dx = wall.end[0] - wall.start[0];
  const dy = wall.end[1] - wall.start[1];
  const length = Math.hypot(dx, dy) || 1;
  return [dx / length, dy / length];
}

const span = (wall: WallSegment) =>
  Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1]);

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));
